/* eslint-disable max-lines-per-function */
import { inject, Injectable } from '@angular/core';

import {
  AdditionalUserSelectionOption,
  AdditionalUserSelectionOptionKey,
  User,
} from '@core/account-types';
import { I18nService } from '@core/i18n-legacy';

@Injectable({ providedIn: 'root' })
export class AdditionalUserSelectionOptionsService {
  private readonly i18n = inject(I18nService);

  private readonly defaultOptions: AdditionalUserSelectionOption[] = [
    {
      key: AdditionalUserSelectionOptionKey.AdminIdsAddRecruiter,
      heading: this.i18n.translate(
        'app.automated_actions.action_editor.additional_user_selection_options.recruiter.heading',
      ),
      subheading: this.i18n.translate(
        'app.automated_actions.action_editor.additional_user_selection_options.recruiter.subheading',
      ),
      tooltip: this.i18n.translate(
        'app.automated_actions.action_editor.additional_user_selection_options.recruiter.tooltip',
      ),
      isSelected: false,
    },
    {
      key: AdditionalUserSelectionOptionKey.AddRecruiter,
      heading: this.i18n.translate(
        'app.automated_actions.action_editor.additional_user_selection_options.recruiter.heading',
      ),
      subheading: this.i18n.translate(
        'app.automated_actions.action_editor.additional_user_selection_options.recruiter.subheading',
      ),
      tooltip: this.i18n.translate(
        'app.automated_actions.action_editor.additional_user_selection_options.recruiter.tooltip',
      ),
      isSelected: false,
    },
    {
      key: AdditionalUserSelectionOptionKey.AdminIdsAddHiringManager,
      heading: this.i18n.translate(
        'app.automated_actions.action_editor.additional_user_selection_options.hiring_manager.heading',
      ),
      subheading: this.i18n.translate(
        'app.automated_actions.action_editor.additional_user_selection_options.hiring_manager.subheading',
      ),
      tooltip: this.i18n.translate(
        'app.automated_actions.action_editor.additional_user_selection_options.hiring_manager.tooltip',
      ),
      isSelected: false,
    },
    {
      key: AdditionalUserSelectionOptionKey.AddHiringManager,
      heading: this.i18n.translate(
        'app.automated_actions.action_editor.additional_user_selection_options.hiring_manager.heading',
      ),
      subheading: this.i18n.translate(
        'app.automated_actions.action_editor.additional_user_selection_options.hiring_manager.subheading',
      ),
      tooltip: this.i18n.translate(
        'app.automated_actions.action_editor.additional_user_selection_options.hiring_manager.tooltip',
      ),
      isSelected: false,
    },
    {
      key: AdditionalUserSelectionOptionKey.AdminIdsAddAdminPerformingAction,
      heading: this.i18n.translate(
        'app.automated_actions.action_editor.additional_user_selection_options.admin_performing_action.heading',
      ),
      subheading: this.i18n.translate(
        'app.automated_actions.action_editor.additional_user_selection_options.admin_performing_action.subheading',
      ),
      tooltip: this.i18n.translate(
        'app.automated_actions.action_editor.additional_user_selection_options.admin_performing_action.tooltip',
      ),
      isSelected: false,
    },
    {
      key: AdditionalUserSelectionOptionKey.AddAdminPerformingAction,
      heading: this.i18n.translate(
        'app.automated_actions.action_editor.additional_user_selection_options.admin_performing_action.heading',
      ),
      subheading: this.i18n.translate(
        'app.automated_actions.action_editor.additional_user_selection_options.admin_performing_action.subheading',
      ),
      tooltip: this.i18n.translate(
        'app.automated_actions.action_editor.additional_user_selection_options.admin_performing_action.tooltip',
      ),
      isSelected: false,
    },
  ];

  public getOptions({
    withoutBadge,
    action,
    recruiter,
    hiringManager,
  }: {
    withoutBadge?: boolean;
    action: {
      adminIdsAddHiringManager?: boolean;
      addHiringManager?: boolean;
      adminIdsAddRecruiter?: boolean;
      addRecruiter?: boolean;
      adminIdsAddAdminPerformingAction?: boolean;
      addAdminPerformingAction?: boolean;
    };
    recruiter: User | null;
    hiringManager: User | null;
  }): AdditionalUserSelectionOption[] {
    return this.getDefaultOptions(action).map(option => {
      if (option.key === AdditionalUserSelectionOptionKey.AdminIdsAddHiringManager) {
        return {
          ...option,
          isSelected: !!action.adminIdsAddHiringManager,
          badge:
            !withoutBadge && !hiringManager
              ? {
                  icon: 'warning',
                  color: 'warning-tint',
                  label: this.i18n.translate('app.common.not_assigned'),
                }
              : undefined,
        };
      }

      if (option.key === AdditionalUserSelectionOptionKey.AddHiringManager) {
        return {
          ...option,
          isSelected: !!action.addHiringManager,
          badge:
            !withoutBadge && !hiringManager
              ? {
                  icon: 'warning',
                  color: 'warning-tint',
                  label: this.i18n.translate('app.common.not_assigned'),
                }
              : undefined,
        };
      }

      if (option.key === AdditionalUserSelectionOptionKey.AdminIdsAddRecruiter) {
        return {
          ...option,
          isSelected: !!action.adminIdsAddRecruiter,
          badge:
            !withoutBadge && !recruiter
              ? {
                  icon: 'warning',
                  color: 'warning-tint',
                  label: this.i18n.translate('app.common.not_assigned'),
                }
              : undefined,
        };
      }

      if (option.key === AdditionalUserSelectionOptionKey.AddRecruiter) {
        return {
          ...option,
          isSelected: !!action.addRecruiter,
          badge:
            !withoutBadge && !recruiter
              ? {
                  icon: 'warning',
                  color: 'warning-tint',
                  label: this.i18n.translate('app.common.not_assigned'),
                }
              : undefined,
        };
      }

      if (option.key === AdditionalUserSelectionOptionKey.AdminIdsAddAdminPerformingAction) {
        return {
          ...option,
          isSelected: !!action.adminIdsAddAdminPerformingAction,
        };
      }

      if (option.key === AdditionalUserSelectionOptionKey.AddAdminPerformingAction) {
        return {
          ...option,
          isSelected: !!action.addAdminPerformingAction,
        };
      }

      return {
        ...option,
        isSelected: false,
        showNoAssignedBadge: false,
      };
    });
  }

  private getDefaultOptions(action: {
    adminIdsAddHiringManager?: boolean;
    addHiringManager?: boolean;
    adminIdsAddRecruiter?: boolean;
    addRecruiter?: boolean;
    adminIdsAddAdminPerformingAction?: boolean;
    addAdminPerformingAction?: boolean;
  }): AdditionalUserSelectionOption[] {
    return this.defaultOptions.filter(option => action[option.key] !== undefined);
  }
}
